import { CircleAlert, Lock } from 'lucide-react';
import { createElement, type ReactNode } from 'react';
import type { SettingLocation } from '../api/types';
import { externalTriggerKind } from '../lib/externalTriggers';
import { formatDateTime, formatDay, formatRelative } from '../lib/format';
import { strings } from '../strings';
import type { RowStatus, ValueCell, VariableRow } from './model';
import { locationEntry } from './registry';
import { RevealCell } from './RevealCell';
import { statusIcon } from './statusIcons';

const s = strings.variables;
const e = strings.externalTriggers;

/** The location's icon tile, label and kind tag (Source column, spec 6.10.5). */
export function SourceChip({
  location,
  kind,
  compact,
}: {
  location: SettingLocation;
  kind?: string | null;
  compact?: boolean;
}) {
  const entry = locationEntry(location);
  const label = entry.label(location);
  return (
    <span className="var-source" title={label}>
      <span className={`var-icon-tile var-tone-${entry.tone}`} aria-hidden>
        {createElement(entry.icon, { className: 'size-3.5' })}
      </span>
      <span className="truncate">{label}</span>
      {!compact && kind && kind !== 'declared' && (
        <span className="var-kind">{s.kinds[kind as 'system']}</span>
      )}
    </span>
  );
}

/** One status badge: icon, text and tone (never colour alone). */
export function StatusBadgeView({ status }: { status: RowStatus }) {
  return (
    <span className={`badge tone-${status.tone}`} title={status.help || undefined}>
      {createElement(statusIcon(status.id), { 'aria-hidden': true, className: 'size-3.5' })}
      {status.label}
    </span>
  );
}

/** The worst status and "+n" for the others (Status column, spec 6.10.7). */
export function StatusCellView({ statuses }: { statuses: readonly RowStatus[] }) {
  if (statuses.length === 0) return <span className="text-fg-faint">{strings.common.dash}</span>;
  const [first, ...rest] = statuses;
  return (
    <span className="flex min-w-0 items-center gap-1">
      <StatusBadgeView status={first} />
      {rest.length > 0 && (
        <span
          className="text-[11.5px] font-semibold text-fg-muted"
          title={rest.map((r) => r.label).join(', ')}
        >
          {s.table.moreStatuses(rest.length)}
          <span className="sr-only">: {rest.map((r) => r.label).join(', ')}</span>
        </span>
      )}
    </span>
  );
}

/** The Current value in source cell (spec 6.10.6). `action` is the cell's inline action (Set value). */
export function ValueCellView({
  cell,
  action,
  now,
  tabbable,
}: {
  cell: ValueCell;
  action?: ReactNode;
  /** For tests; defaults to the current time. */
  now?: number;
  /** Controls inside are Tab stops (the details pane); in grids the cell's keys reach them. */
  tabbable?: boolean;
}) {
  const v = s.value;
  switch (cell.kind) {
    case 'text':
      return (
        <span className="var-value" aria-label={cell.label} title={cell.text}>
          <span
            className={`truncate font-mono ${cell.muted ? 'font-sans text-fg-muted italic' : ''} ${cell.struck ? 'line-through' : ''}`}
          >
            {cell.text}
          </span>
          {cell.applied !== undefined && (
            <span className="shrink-0 text-[12px] text-fg-muted">{v.applied(cell.applied)}</span>
          )}
          {cell.cloud !== undefined && (
            <span className="var-cloud truncate text-[12px]" title={cell.cloud}>
              {cell.cloud}
            </span>
          )}
        </span>
      );
    case 'set-per-deployment':
      return (
        <span className="var-value text-fg-muted" aria-label={cell.label}>
          <Lock aria-hidden className="size-3.5 shrink-0" />
          {v.setPerDeployment}
        </span>
      );
    case 'secret-set':
      return (
        <span className="var-value" aria-label={cell.label}>
          <Lock aria-hidden className="size-3.5 shrink-0 text-fg-muted" />
          <span className="font-semibold">{v.set}</span>
          {cell.version && <span className="font-mono text-[12px]">{cell.version}</span>}
          <span className="truncate text-[12px] text-fg-muted" title={formatDateTime(cell.at)}>
            · {formatRelative(cell.at, now)} {cell.by ? strings.common.by(cell.by) : v.outsideNebisys}
          </span>
          {cell.note && (
            <span className="truncate text-[12px] text-fg-muted" title={cell.noteHelp}>
              · {cell.note}
            </span>
          )}
        </span>
      );
    case 'secret-system':
      return (
        <span className="var-value" aria-label={cell.label}>
          <Lock aria-hidden className="size-3.5 shrink-0 text-fg-muted" />
          <span className="font-semibold">{v.set}</span>
          {cell.note ? (
            <span className="truncate text-[12px] text-fg-muted" title={cell.noteHelp}>
              · {cell.note}
            </span>
          ) : (
            <span className="truncate text-[12px] text-fg-muted">· {v.byNebisys}</span>
          )}
        </span>
      );
    case 'secret-revealable':
      return <RevealCell cell={cell} tabbable={tabbable} now={now} />;
    case 'no-value':
      return (
        <span className="var-value" aria-label={cell.label}>
          <span className="badge tone-danger">
            <CircleAlert aria-hidden className="size-3.5" />
            {v.noValue}
          </span>
          {action}
        </span>
      );
    case 'store-copy':
      return (
        <span className="var-value" aria-label={cell.label}>
          <span className="font-semibold">{v.inStore(cell.store)}</span>
          <span className="truncate text-[12px] text-fg-muted" title={formatDateTime(cell.at)}>
            · {v.copiedAtLastApply} {v.copiedDetail(cell.version, formatRelative(cell.at, now))}
          </span>
        </span>
      );
    case 'generated':
      return (
        <span className="var-value font-semibold" aria-label={cell.label}>
          {v.generated}
        </span>
      );
    case 'unmanaged':
      return (
        <span className="var-value text-fg-muted" aria-label={cell.label}>
          {v.unmanaged}
        </span>
      );
    case 'deleted':
      return (
        <span className="var-value" aria-label={cell.label}>
          <span className="truncate text-[12px]" title={formatDateTime(cell.deletedAt)}>
            {v.deleted(formatRelative(cell.deletedAt, now))} ·{' '}
            {cell.recoverability === 'recoverable'
              ? v.recoverableUntil(formatDay(cell.recoverableUntil))
              : cell.recoverability === 'not-recoverable'
                ? v.notRecoverable
                : v.recoverabilityUnknown}
          </span>
          {action}
        </span>
      );
    case 'unreachable':
      return (
        <span className="var-value" aria-label={cell.label}>
          {cell.version && (
            <span className="font-mono text-[12px] text-fg-muted">
              {v.set} {cell.version}
            </span>
          )}
          <span className="badge tone-danger">{v.vaultUnreachable}</span>
          {action}
        </span>
      );
    case 'unknown':
      return (
        <span className="var-value text-fg-muted" aria-label={cell.label}>
          {v.unknown}
        </span>
      );
  }
}

/** The Variable column: lock, name, "from property …" and "not an environment variable". */
/**
 * The external trigger chip (feature spec 16 9.7): the kind's icon and the trigger's name in a neutral outline. Not a
 * control: the cell's accessible name carries it (variableCellName).
 */
export function ExternalTriggerChip({ trigger }: { trigger: { name: string; kind: string } }) {
  const kind = externalTriggerKind(trigger.kind);
  return (
    <span
      className="var-ext-chip"
      title={e.chipTooltip(kind.label)}
      aria-hidden
      data-testid="external-trigger-chip"
    >
      {createElement(kind.icon, { className: 'size-3 shrink-0' })}
      <span className="truncate">{trigger.name}</span>
    </span>
  );
}

export function VariableNameView({ row }: { row: VariableRow }) {
  return (
    <span className="flex min-w-0 flex-col">
      <span className="flex min-w-0 items-center gap-1.5">
        {row.secret && (
          <Lock aria-label={s.table.lock} role="img" className="size-3.5 shrink-0 text-fg-muted" />
        )}
        <span className="truncate font-mono font-semibold" title={row.displayName}>
          {row.displayName}
        </span>
        {row.externalTrigger && <ExternalTriggerChip trigger={row.externalTrigger} />}
        {row.problems.length > 0 && (
          <span title={row.problems.join('\n')} className="inline-flex text-danger">
            <CircleAlert aria-hidden className="size-3.5" />
            <span className="sr-only">{row.problems.join(' ')}</span>
          </span>
        )}
      </span>
      {row.property && (
        <span className="truncate text-[11px] text-fg-muted">{s.table.fromProperty(row.property)}</span>
      )}
      {row.delivery && !row.delivery.exposedAsEnv && (
        <span className="truncate text-[11px] text-fg-muted">{s.table.notEnv}</span>
      )}
    </span>
  );
}

/**
 * A secret's name in its store (feature spec 15 19.6). When it starts with the vault's prefix (`container`), the prefix
 * renders muted and is truncated first, so the secret's own name stays complete ("…/payment-api-key"); the tooltip and
 * the accessible name are the full name.
 */
export function NativeName({
  name,
  prefix,
  className = '',
  suffix = '',
}: {
  name: string;
  prefix?: string | null;
  className?: string;
  suffix?: string;
}) {
  if (!prefix || !name.startsWith(prefix) || name.length === prefix.length)
    return (
      <span className={`block truncate font-mono ${className}`} title={name}>
        {name}
        {suffix}
      </span>
    );
  return (
    <span
      className={`var-native-name font-mono ${className}`}
      title={name}
      role="img"
      aria-label={name}
      data-testid="native-name"
    >
      <span className="var-native-prefix" aria-hidden>
        <bdi>{prefix}</bdi>
      </span>
      <span className="var-native-own" aria-hidden>
        {name.slice(prefix.length)}
        {suffix}
      </span>
    </span>
  );
}
